import React from 'react';
import type { Point, Wall, Room } from '../types';
import { 
  WallSnappingSystem, 
  createRoomWallSnappingSystem, 
  snapRoomDuringDrag,
  DEFAULT_SNAP_CONFIG,
  type WallSnapConfig,
  type SnapResult 
} from './wallSnappingSystem';
import { calculateRooms } from './floorPlanUtils';

/**
 * Enhanced room dragging hook that integrates professional wall snapping
 * Use this to replace or enhance the existing room dragging in FloorPlanCreator
 */
export interface RoomDragWithSnappingProps {
  // Current floor plan state
  currentWalls: Wall[];
  existingRooms: Room[];
  selectedRoom: Room | null;
  
  // Drag state
  isDraggingRoom: boolean;
  dragStartPoint: Point | null;
  currentMousePoint: Point;
  
  // Configuration
  scale: number;
  snapConfig?: Partial<WallSnapConfig>;
  
  // Callbacks
  onRoomPositionUpdate: (newPosition: Point, snapInfo: SnapResult) => void;
  onSnapFeedback: (snapResult: SnapResult) => void;
}

/**
 * Enhanced room dragging with professional CAD-style wall snapping
 */
export function useRoomDragWithWallSnapping({
  currentWalls,
  existingRooms,
  selectedRoom,
  isDraggingRoom,
  dragStartPoint,
  currentMousePoint,
  scale,
  snapConfig,
  onRoomPositionUpdate,
  onSnapFeedback
}: RoomDragWithSnappingProps) {
  
  const handleRoomDragWithSnapping = React.useCallback(() => {
    if (!isDraggingRoom || !selectedRoom || !dragStartPoint) {
      return;
    }

    // Use the professional wall snapping system
    const snapResult = snapRoomDuringDrag(
      selectedRoom,
      dragStartPoint,
      currentMousePoint,
      currentWalls,
      existingRooms,
      scale,
      snapConfig
    );

    // Provide visual feedback about the snap
    onSnapFeedback(snapResult);

    // Calculate the new room position
    const newPosition = snapResult.snappedPoint;
    onRoomPositionUpdate(newPosition, snapResult);

  }, [
    isDraggingRoom,
    selectedRoom,
    dragStartPoint,
    currentMousePoint,
    currentWalls,
    existingRooms,
    scale,
    snapConfig,
    onRoomPositionUpdate,
    onSnapFeedback
  ]);

  return {
    handleRoomDragWithSnapping,
    // Return the current snap state for use in rendering
    getCurrentSnapState: () => {
      if (!isDraggingRoom || !selectedRoom || !dragStartPoint) {
        return null;
      }
      
      return snapRoomDuringDrag(
        selectedRoom,
        dragStartPoint,
        currentMousePoint,
        currentWalls,
        existingRooms,
        scale,
        snapConfig
      );
    }
  };
}

/**
 * Room connection assistant - automatically connects rooms to walls
 */
export class RoomConnectionAssistant {
  private snappingSystem: WallSnappingSystem;
  
  constructor(config?: Partial<WallSnapConfig>) {
    this.snappingSystem = createRoomWallSnappingSystem(config);
  }
  
  /**
   * Automatically connect a room to nearby walls
   */
  public autoConnectRoomToWalls(
    room: Room,
    existingWalls: Wall[],
    maxConnectionDistance: number = 50
  ): { connectedWalls: Wall[], newWalls: Wall[] } {
    const connectedWalls: Wall[] = [];
    const newWalls: Wall[] = [];
    
    if (!room.walls || room.walls.length < 3) {
      return { connectedWalls, newWalls };
    }
    
    // Check each room wall for potential connections
    for (const roomWall of room.walls) {
      const currentPoint = roomWall.start;
      const nextPoint = roomWall.end;
      
      // Find the closest wall to this room edge
      let closestWall: Wall | null = null;
      let minDistance = maxConnectionDistance;
      
      for (const wall of existingWalls) {
        const distance = this.getDistanceFromPointToLine(currentPoint, wall.start, wall.end);
        if (distance < minDistance) {
          minDistance = distance;
          closestWall = wall;
        }
      }
      
      if (closestWall) {
        // Check if we should connect this room edge to the wall
        const shouldConnect = this.shouldConnectRoomEdgeToWall(
          { start: currentPoint, end: nextPoint },
          closestWall,
          minDistance
        );
        
        if (shouldConnect) {
          connectedWalls.push(closestWall);
          
          // Create connecting walls if needed
          const connectingWalls = this.createConnectingWalls(
            { start: currentPoint, end: nextPoint },
            closestWall
          );
          newWalls.push(...connectingWalls);
        }
      }
    }
    
    return { connectedWalls, newWalls };
  }
  
  private getDistanceFromPointToLine(point: Point, lineStart: Point, lineEnd: Point): number {
    const A = point.x - lineStart.x;
    const B = point.y - lineStart.y;
    const C = lineEnd.x - lineStart.x;
    const D = lineEnd.y - lineStart.y;
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    
    if (lenSq === 0) return Math.sqrt(A * A + B * B);
    
    let param = dot / lenSq;
    param = Math.max(0, Math.min(1, param));
    
    const xx = lineStart.x + param * C;
    const yy = lineStart.y + param * D;
    
    const dx = point.x - xx;
    const dy = point.y - yy;
    
    return Math.sqrt(dx * dx + dy * dy);
  }
  
  private shouldConnectRoomEdgeToWall(
    roomEdge: { start: Point, end: Point },
    wall: Wall,
    distance: number
  ): boolean {
    // Add logic to determine if connection makes sense
    const angleThreshold = 15; // degrees
    const distanceThreshold = 25; // pixels
    
    if (distance > distanceThreshold) return false;
    
    // Check angle alignment
    const roomVector = {
      x: roomEdge.end.x - roomEdge.start.x,
      y: roomEdge.end.y - roomEdge.start.y
    };
    
    const wallVector = {
      x: wall.end.x - wall.start.x,
      y: wall.end.y - wall.start.y
    };
    
    const angle = this.getAngleBetweenVectors(roomVector, wallVector);
    const parallelAngle = Math.min(Math.abs(angle), Math.abs(180 - Math.abs(angle)));
    
    return parallelAngle <= angleThreshold;
  }
  
  private createConnectingWalls(
    roomEdge: { start: Point, end: Point },
    targetWall: Wall
  ): Wall[] {
    // Create walls to connect room edge to target wall
    // This is simplified - in a real implementation you'd want more sophisticated logic
    return [];
  }
  
  private getAngleBetweenVectors(v1: Point, v2: Point): number {
    const dot = v1.x * v2.x + v1.y * v2.y;
    const det = v1.x * v2.y - v1.y * v2.x;
    return Math.atan2(det, dot) * (180 / Math.PI);
  }
}

/**
 * Integration helper for FloorPlanCreator.tsx
 * This shows how to modify the existing handleCanvasMouseMove function
 */
export function integrateWallSnappingWithFloorPlanCreator() {
  return `
// Add this to your FloorPlanCreator.tsx imports:
import { 
  snapRoomDuringDrag, 
  createRoomWallSnappingSystem,
  type SnapResult 
} from '../utils/wallSnappingSystem';

// Add this state for snap feedback:
const [currentSnapResult, setCurrentSnapResult] = useState<SnapResult | null>(null);
const [showSnapPreview, setShowSnapPreview] = useState(false);

// Modify your existing handleCanvasMouseMove function:
const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
  const rawPoint = getCanvasCoordinates(e.clientX, e.clientY);
  
  // ... existing code ...
  
  // ENHANCED: Room dragging with professional wall snapping
  if (isDraggingRoom && selectedRoom && roomStart) {
    const currentRooms = calculateRooms(
      floorPlans[currentFloor] || [], 
      roomCustomLabels, 
      'mouseMove'
    );
    
    // Use professional wall snapping
    const snapResult = snapRoomDuringDrag(
      selectedRoom,
      roomStart, // dragStartPoint
      rawPoint,  // currentMousePoint
      floorPlans[currentFloor] || [],
      currentRooms,
      scale,
      {
        // Customize snapping behavior
        magneticSnapDistance: 30,
        endpointSnapDistance: 20,
        showSnapPreview: true,
        snapFeedbackColor: '#00FF00'
      }
    );
    
    // Update snap feedback for visual indicators
    setCurrentSnapResult(snapResult);
    setShowSnapPreview(snapResult.snapType !== 'none');
    
    // Use snapped position for room placement
    setRoomEnd(snapResult.snappedPoint);
    return;
  }
  
  // ... rest of existing code ...
};

// Add this function to render snap feedback:
const drawSnapFeedback = (ctx: CanvasRenderingContext2D) => {
  if (!showSnapPreview || !currentSnapResult) return;
  
  const { snapType, snappedPoint, previewLine, snapFeedbackColor } = currentSnapResult;
  
  ctx.save();
  ctx.strokeStyle = snapFeedbackColor || '#00FF00';
  ctx.lineWidth = 2;
  ctx.setLineDash([5, 5]);
  
  // Draw snap indicator at snap point
  ctx.beginPath();
  ctx.arc(snappedPoint.x, snappedPoint.y, 8, 0, 2 * Math.PI);
  ctx.stroke();
  
  // Draw preview line if available (for extensions, etc.)
  if (previewLine) {
    ctx.beginPath();
    ctx.moveTo(previewLine.start.x, previewLine.start.y);
    ctx.lineTo(previewLine.end.x, previewLine.end.y);
    ctx.stroke();
  }
  
  // Draw snap type label
  ctx.fillStyle = snapFeedbackColor || '#00FF00';
  ctx.font = '12px Arial';
  ctx.fillText(
    snapType.toUpperCase(), 
    snappedPoint.x + 12, 
    snappedPoint.y - 12
  );
  
  ctx.restore();
};

// Modify your canvas drawing function to include snap feedback:
const draw = () => {
  // ... existing drawing code ...
  
  // Add snap feedback rendering
  if (showSnapPreview) {
    drawSnapFeedback(ctx);
  }
  
  // ... rest of drawing code ...
};
`;
}

/**
 * Example usage in FloorPlanCreator component
 */
export const INTEGRATION_EXAMPLE = `
// In your FloorPlanCreator.tsx component:

import { 
  snapRoomDuringDrag, 
  RoomConnectionAssistant,
  type SnapResult 
} from '../utils/roomDragWithWallSnapping';

// Add to component state:
const [snapFeedback, setSnapFeedback] = useState<SnapResult | null>(null);
const roomConnectionAssistant = useMemo(() => new RoomConnectionAssistant(), []);

// In your handleCanvasMouseMove function:
if (isDraggingRoom && selectedRoom && roomStart) {
  const snapResult = snapRoomDuringDrag(
    selectedRoom,
    roomStart,
    rawPoint,
    currentWalls,
    currentRooms,
    scale
  );
  
  setSnapFeedback(snapResult);
  setRoomEnd(snapResult.snappedPoint);
}

// In your handleMouseUp function:
if (selectedRoom && snapFeedback) {
  // Auto-connect room to nearby walls when drag ends
  const connections = roomConnectionAssistant.autoConnectRoomToWalls(
    selectedRoom,
    currentWalls,
    50 // max connection distance
  );
  
  // Add new connecting walls if any
  if (connections.newWalls.length > 0) {
    setFloorPlans(prev => ({
      ...prev,
      [currentFloor]: [...(prev[currentFloor] || []), ...connections.newWalls]
    }));
  }
}
`; 